import copy from "copy-to-clipboard";
import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import { UnstyledButton } from "../../../common/buttons/default-button";
import { CrossIcon } from "../../../common/svg/cross-icon";
import { StaticAssets } from "../../../styles/custom-assets";
import { theme } from "../../../styles/theme";

interface AccountFieldRowProps {
  label: string;
  value: string;
  /** Editable fields sit in a white box and can be cleared; the others stay read-only and grey. */
  onChange?: (value: string) => void;
  onSubmit?: () => void;
}

export function AccountFieldRow({ label, value, onChange, onSubmit }: Readonly<AccountFieldRowProps>) {
  const { formatMessage } = useIntl();
  const editable = !!onChange;

  return (
    <Row>
      <LabelRow>
        <Label>{label}</Label>
        <CopyButton aria-label={formatMessage("accountDetails.clipboard", { name: label })} onClick={() => copy(value)}>
          <CopyIcon src={StaticAssets.copy} alt="" />
        </CopyButton>
      </LabelRow>
      <Box $editable={editable}>
        {editable ? (
          <>
            <Input
              value={value}
              onChange={(event) => onChange?.(event.target.value)}
              onBlur={() => onSubmit?.()}
              onKeyDown={(event) => event.key === "Enter" && onSubmit?.()}
            />
            {!!value && (
              <ClearButton aria-label={formatMessage("common.clear")} onClick={() => onChange?.("")}>
                <CrossIcon width={12} height={12} />
              </ClearButton>
            )}
          </>
        ) : (
          <ReadOnlyValue>{value}</ReadOnlyValue>
        )}
      </Box>
    </Row>
  );
}

const Row = styled.div`
  display: flex;
  flex-direction: column;
`;

const LabelRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const Label = styled.span`
  ${theme.text};
  font-size: 13px;
  line-height: 18px;
  color: ${theme.colors.blue[800]};
`;

const CopyButton = styled(UnstyledButton)`
  display: flex;
  padding: 2px;
`;

const CopyIcon = styled.img`
  width: 16px;
  height: 16px;
`;

const Box = styled.div<{ $editable: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid ${(props) => (props.$editable ? "#d9d9d9" : theme.colors.gray[100])};
  border-radius: 8px;
  background-color: ${(props) => (props.$editable ? "#ffffff" : "#fbfbfb")};
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background-color: transparent;
  font-family: inherit;
  font-size: 14px;
  color: ${theme.colors.blue[800]};
`;

const ClearButton = styled(UnstyledButton)`
  display: flex;
  flex-shrink: 0;
  padding: 2px;
`;

const ReadOnlyValue = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 14px;
  color: ${theme.colors.gray[500]};
  text-overflow: ellipsis;
  white-space: nowrap;
`;
